import { CalculateConfigurationParticipantParticipantSourceDataAllocations, ConfigurationParticipant as ConfigurationParticipantDto, ConfigurationStatus, SaveConfigurationParticipant, SourceDataAllocationType } from "@/api/dtos";
import SetLinks from "@/components/layouts/SetLinks";
import useConfigurationParticipant from "@/hooks/data/useConfigurationParticipant";
import { InputAdornment, darken, lighten, styled } from '@mui/material';
import { grey } from "@mui/material/colors";
import { FC, useCallback, useContext, useMemo } from "react";

import ConfigurationContextTemplate, { IConfigurationContextTemplateProps } from "@/components/forms/templates/ConfigurationContextTemplate";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import TrialContext from "@/contexts/data/TrialContext";
import { ConfigurationParticipantsInstitutionCreateLink, ConfigurationParticipantsParticipantCreateLink, ConfigurationParticipantsTrialCreateLink } from "@/contexts/layouts/LinkContext";
import { useLocation } from "react-router-dom";


const allocationTypes = [
    SourceDataAllocationType.Constant,
    SourceDataAllocationType.Inherit
];

const statuses = [
    ConfigurationStatus.Disabled,
    ConfigurationStatus.ReadOnly,
    ConfigurationStatus.Enabled
];

const OriginalName = styled(InputAdornment)(({ theme }) => ({
    background: theme.palette.mode === 'dark' ? darken(grey[600], 0.5): lighten(grey[600], 0.5),
    alignSelf: 'stretch',
    maxHeight: 'unset',
    height: 'unset',
    padding: theme.spacing(0, 2, 0, 2),
    marginRight: theme.spacing(0),
    borderTopRightRadius: theme.shape.borderRadius,
    borderBottomRightRadius: theme.shape.borderRadius,
    gap: theme.spacing(2),
    justifyContent: 'space-between'
}));


const ConfigurationParticipant: FC = () => {
    const { data: trial, oid: trialOid } = useContext(TrialContext);
    const { data: participant } = useContext(ParticipantContext);

    const contextQuery = useMemo(() => {
        if (!participant?.id) {
            return undefined;
        }

        return {
            trialId: participant.trialId,
            masterGroupId: participant.masterGroupId,
            coordinatingGroupId: participant.coordinatingGroupId,
            institutionId: participant.institutionId,
            participantId: participant.id
        }
    }, [participant]);

    const { data: configurationParticipant, loading: configurationParticipantLoading, validating: configurationParticipantValidating } = useConfigurationParticipant(contextQuery, { revalidateOnFocus: false });

    const loading = configurationParticipantLoading;
    const validating = configurationParticipantValidating;

    const location = useLocation();

    const returnUrl = useMemo(() => {
        if (location.state?.from) {
            return `${location.state.from.pathname}${location.state.from.search}`
        }
        
        return `/${trialOid}/configuration/participants`;
    }, [trialOid, location])

    const onSave = useCallback<IConfigurationContextTemplateProps<ConfigurationParticipantDto>['onSave']>(async (client, data, allocations, updates) => {
        await client.post(new SaveConfigurationParticipant({ data: data, allocations: allocations, participantSourceDataAllocationUpdates: updates}));
    }, []);

    const onCalculateAllocations = useCallback<IConfigurationContextTemplateProps<ConfigurationParticipantDto>['onCalculateAllocations']>(async (client, contextQuery, data, allocations) => {
        const response = await client.post(new CalculateConfigurationParticipantParticipantSourceDataAllocations({ 
            ...contextQuery,
            configurationParticipant: data, 
            allocations: allocations
        }));

        return response;
    }, []);
    
    const onValidate = useCallback<IConfigurationContextTemplateProps<ConfigurationParticipantDto>['onValidate']>(async (client, errors, formState) => {

        return errors;
    }, []);

    return (
        <>
            <SetLinks 
                createTrialLink={ConfigurationParticipantsTrialCreateLink} 
                createInstitutionLink={ConfigurationParticipantsInstitutionCreateLink} 
                createParticipantLink={ConfigurationParticipantsParticipantCreateLink} 
            />
            <ConfigurationContextTemplate
                allocationTypes={allocationTypes}
                entityName="Participant"
                title={participant?.studyNumber}
                onCalculateAllocations={onCalculateAllocations}
                onSave={onSave}
                onValidate={onValidate}
                statuses={statuses}
                configuration={configurationParticipant}
                configurationLoading={loading}
                configurationValidating={validating}
                contextQuery={contextQuery}
                returnUrl={returnUrl}
            />
        </>
    )
};

export default ConfigurationParticipant;